import type { KitItem, OrcamentoResultado } from './calc';
import { brl } from './calc';
import { imprimirHtml } from '@/lib/arquivos';

export interface DadosCliente {
  nome: string;
  cidade: string;
  uf: string;
  animais: string;
}

export interface DadosProposta {
  cliente: DadosCliente;
  itens: KitItem[];
  resultado: OrcamentoResultado;
  softwarePlano: string;
  softwareMensal: number;
  softwareMeses: number;
  prazoEntrega: string;
  validadeDias: number;
  garantiaMeses: number;
}

const esc = (s: string) =>
  String(s || '').replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] || c
  );

const pad2 = (n: number) => String(n).padStart(2, '0');

// Proposta comercial em estilo de carta clássica: wordmark serifada, navy sóbrio,
// muito espaço em branco e filetes finos. Sem travessões e sem caixas tingidas.
export function gerarPropostaPdf(d: DadosProposta) {
  const origin = window.location.origin;
  const agora = new Date();
  const dataStr = `${pad2(agora.getDate())}/${pad2(agora.getMonth() + 1)}/${agora.getFullYear()}`;
  const numero = `CT-${agora.getFullYear()}${pad2(agora.getMonth() + 1)}${pad2(agora.getDate())}-${pad2(agora.getHours())}${pad2(agora.getMinutes())}`;

  const localCliente = [d.cliente.cidade, d.cliente.uf].filter(Boolean).join('/');
  const clienteMeta = [localCliente, d.cliente.animais ? `${d.cliente.animais} animais` : '']
    .filter(Boolean)
    .join(', ');
  const softwareTotal = d.softwareMensal * d.softwareMeses;

  const linhasKit = d.itens
    .filter((i) => i.quantidade > 0)
    .map(
      (i) =>
        `<tr><td>${esc(i.produto.nome)}${i.produto.modelo && !i.produto.nome.includes(i.produto.modelo) ? ` <span class="muted">(${esc(i.produto.modelo)})</span>` : ''}</td><td class="num">${i.quantidade}</td></tr>`
    )
    .join('');

  const linhaSoftware =
    softwareTotal > 0
      ? `<tr><td>Software ${esc(d.softwarePlano)} <span class="muted">(${brl(d.softwareMensal)}/mês, ${d.softwareMeses} meses)</span></td><td class="num">${brl(softwareTotal)}</td></tr>`
      : '';

  const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
<title>Proposta ${numero} Curral Tech</title>
<style>
  :root{ --navy:#0b3d5c; --ink:#1a1f24; --muted:#6b7580; --line:#e3e8ec; }
  *{ box-sizing:border-box; }
  html,body{ margin:0; }
  @page{ size:A4; margin:14mm; }
  body{
    font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif;
    color:var(--ink); font-size:12px; line-height:1.4; padding:28px 34px;
    -webkit-print-color-adjust:exact; print-color-adjust:exact;
  }
  .serif{ font-family:Georgia,"Times New Roman",serif; }
  .top{ display:flex; justify-content:space-between; align-items:flex-start; gap:24px; }
  .brand{ display:flex; align-items:center; gap:12px; }
  .brand img{ height:40px; filter:grayscale(1) contrast(1.05); -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  .brand .name{ font-size:18px; font-weight:700; letter-spacing:2px; color:#1a1f24; }
  .brand .tag{ font-size:10px; color:var(--muted); letter-spacing:.3px; }
  .meta{ text-align:right; font-size:11px; color:var(--muted); line-height:1.6; }
  .meta b{ color:var(--ink); font-weight:600; }
  .rule{ height:2px; background:var(--navy); margin:12px 0 16px; }
  h1{ font-size:19px; color:var(--navy); margin:0 0 2px; font-weight:600; letter-spacing:.2px; }
  .subtitle{ color:var(--muted); font-size:12px; margin-bottom:14px; }
  .client{ border-bottom:1px solid var(--line); padding-bottom:10px; margin-bottom:12px; }
  .client .lbl{ font-size:10px; text-transform:uppercase; letter-spacing:1px; color:var(--muted); margin-bottom:2px; }
  .client .cname{ font-size:15px; font-weight:600; color:var(--ink); }
  .client .cmeta{ font-size:12px; color:var(--muted); margin-top:1px; }
  h2{ font-size:10.5px; text-transform:uppercase; letter-spacing:1.2px; color:var(--navy); margin:14px 0 5px; font-weight:700; }
  table{ width:100%; border-collapse:collapse; }
  th{ text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:.6px; color:var(--muted); border-bottom:1.5px solid var(--line); padding:6px 4px; font-weight:600; }
  td{ padding:6px 4px; border-bottom:1px solid var(--line); }
  .num{ text-align:right; white-space:nowrap; }
  .muted{ color:var(--muted); }
  .invest .total td{ font-size:15px; font-weight:700; color:var(--navy); border-top:2px solid var(--navy); border-bottom:none; padding-top:9px; }
  .note{ font-size:10.5px; color:var(--muted); margin-top:6px; }
  .terms{ margin-top:14px; }
  .terms .row{ display:flex; padding:5px 0; border-bottom:1px solid var(--line); font-size:12px; }
  .terms .k{ width:180px; color:var(--muted); }
  .terms .v{ color:var(--ink); font-weight:600; }
  .foot{ margin-top:16px; padding-top:10px; border-top:1px solid var(--line); font-size:10.5px; color:var(--muted); display:flex; justify-content:space-between; }
  @media print{ body{ padding:0; } }
</style></head>
<body onload="window.print()">
  <div class="top">
    <div class="brand">
      <img src="${origin}/logo.png" alt="Curral Tech">
      <div>
        <div class="name serif">CURRAL TECH</div>
        <div class="tag">GESTÃO PECUÁRIA INTELIGENTE</div>
      </div>
    </div>
    <div class="meta">
      Proposta <b>${numero}</b><br>
      Data <b>${dataStr}</b><br>
      Validade <b>${d.validadeDias} dias</b>
    </div>
  </div>

  <div class="rule"></div>

  <h1 class="serif">Proposta Comercial</h1>
  <div class="subtitle">Kit de identificação eletrônica por radiofrequência (RFID) e software de gestão de rebanho.</div>

  <div class="client">
    <div class="lbl">Preparado para</div>
    <div class="cname">${esc(d.cliente.nome) || 'Cliente'}</div>
    ${clienteMeta ? `<div class="cmeta">${esc(clienteMeta)}</div>` : ''}
  </div>

  <h2>Composição do kit</h2>
  <table>
    <thead><tr><th>Item</th><th class="num">Quantidade</th></tr></thead>
    <tbody>${linhasKit}</tbody>
  </table>

  <h2>Investimento</h2>
  <table class="invest">
    <tbody>
      <tr><td>Equipamentos (leitores e aplicador)</td><td class="num">${brl(d.resultado.hardwareVenda)}</td></tr>
      ${d.resultado.brincoQtd > 0 ? `<tr><td>Brincos de identificação (${d.resultado.brincoQtd} unidades)</td><td class="num">${brl(d.resultado.brincoLiquido)}</td></tr>` : ''}
      <tr><td>Frete até o destino</td><td class="num">${brl(d.resultado.freteNacional)}</td></tr>
      <tr class="total"><td>Total à vista</td><td class="num">${brl(d.resultado.totalGeral)}</td></tr>
      ${linhaSoftware}
    </tbody>
  </table>
  ${d.cliente.cidade ? `<div class="note">Valores com entrega posta em ${esc(d.cliente.cidade)}.</div>` : ''}

  <h2>Condições</h2>
  <div class="terms">
    <div class="row"><div class="k">Prazo de entrega</div><div class="v">${esc(d.prazoEntrega) || 'a combinar'}</div></div>
    <div class="row"><div class="k">Garantia dos equipamentos</div><div class="v">${d.garantiaMeses} meses</div></div>
    <div class="row"><div class="k">Pagamento</div><div class="v">Hardware à vista; software em assinatura mensal.</div></div>
    <div class="row"><div class="k">Inclui</div><div class="v">Treinamento de uso e suporte técnico durante a assinatura.</div></div>
  </div>

  <div class="foot">
    <span>Curral Tech, Campo Grande/MS</span>
    <span>Proposta ${numero}, gerada em ${dataStr}</span>
  </div>
</body></html>`;

  void imprimirHtml(html, `Proposta ${numero}`);
}
